/**
 * Utility to get the first accessible page based on permissions and plan features
 * Used for initial landing page after login and team switching
 */

import { NavigationNode } from '@/components/platform/navigation/NavigationTree';
import {
  Squares2X2Icon,
  ChartPieIcon,
  UsersIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  CodeBracketIcon,
  QueueListIcon,
  PuzzlePieceIcon,
  BeakerIcon,
  SparklesIcon,
  CubeIcon,
  ChartBarIcon,
  Square3Stack3DIcon,
  DocumentTextIcon,
  ComputerDesktopIcon,
  RocketLaunchIcon,
  SunIcon,
  DocumentDuplicateIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { isSponsorshipsEnabled } from '@/lib/platform/config/feature-flags';
import {
  hasAnyValuationPermission as checkHasAnyValuationPermission,
  hasAnySponsorshipPermission as checkHasAnySponsorshipPermission,
  hasRole,
} from '@/lib/platform/utils/permissions-helpers';

/**
 * Navigation tree structure (same as in TailwindSidebar)
 * Priority order from top to bottom:
 * 1. Valuations > Dashboard
 * 2. Valuations > Valuations
 * 3. Valuations > Reports
 * 4. Valuations > Methods
 * 5. Sponsorships > Sponsors
 * 6. Sponsorships > Packages
 * 7. Sponsorships > Library
 * 8. Sponsorships > Proposals
 * 9. Sponsorships > Analytics
 * 10. Platform > Users
 * 11. Platform > Organisation
 * 12. Platform > Billing
 * 13. Platform > Plugins
 * 14. Platform > API
 */
export const navigationTree: NavigationNode[] = [
  {
    id: 'valuations',
    name: 'VALUATIONS',
    icon: RocketLaunchIcon,
    children: [
      {
        id: 'dashboard',
        name: 'Dashboard',
        href: '/products/valuations/dashboard',
        icon: Squares2X2Icon,
        requiredRole: 'dashboard',
      },
      {
        id: 'valuations',
        name: 'Valuations',
        href: '/products/valuations/valuations',
        icon: QueueListIcon,
      },
      {
        id: 'reports',
        name: 'Reports',
        href: '/products/valuations/reports',
        icon: ChartBarIcon,
        requiredRole: 'reports',
      },
      {
        id: 'methods',
        name: 'Methods',
        href: '/products/valuations/methods',
        icon: BeakerIcon,
        requiredRole: 'methods',
      },
    ],
  },
  {
    id: 'sponsorships',
    name: 'SPONSORSHIPS',
    icon: SunIcon,
    // NOTE: No feature check on parent - children have feature checks
    // This makes Sponsorships appear immediately (no API wait) like Valuations and Platform
    children: [
      {
        id: 'sponsors',
        name: 'Sponsors',
        href: '/products/sponsorships/sponsors',
        icon: SparklesIcon,
        feature: 'sponsorshipSponsors',
        requiredRole: 'sponsors',
      },
      {
        id: 'packages',
        name: 'Packages',
        href: '/products/sponsorships/packages',
        icon: CubeIcon,
        feature: 'sponsorshipPackages',
        requiredRole: 'packages',
      },
      {
        id: 'library',
        name: 'Inventory',
        href: '/products/sponsorships/inventory',
        icon: Square3Stack3DIcon,
        feature: 'sponsorshipPackages',
        requiredRole: 'inventory',
      },
      {
        id: 'proposals',
        name: 'Proposals',
        href: '/products/sponsorships/proposals',
        icon: DocumentTextIcon,
        feature: 'sponsorshipPackages',
        requiredRole: 'proposals',
      },
      {
        id: 'analytics',
        name: 'Analytics',
        href: '/products/sponsorships/analytics',
        icon: ChartPieIcon,
        // Enterprise only: all analytics data comes from sponsor_packages
        // (must match the page/API gate on sponsorshipPackages)
        feature: 'sponsorshipPackages',
        requiredRole: 'analytics',
      },
    ],
  },
  {
    id: 'platform',
    name: 'PLATFORM',
    icon: ComputerDesktopIcon,
    children: [
      {
        id: 'settings',
        name: 'Organisation',
        href: '/platform/organisation',
        icon: Cog6ToothIcon,
        requiredRole: 'organisation',
      },
      {
        id: 'users',
        name: 'Users',
        href: '/platform/users',
        icon: UsersIcon,
        requiredRole: 'users',
      },
      {
        id: 'documents',
        name: 'Documents',
        href: '/platform/documents',
        icon: DocumentDuplicateIcon,
        requiredRole: 'documents',
        feature: 'documents',
      },
      {
        id: 'app-settings',
        name: 'Settings',
        href: '/platform/settings',
        icon: AdjustmentsHorizontalIcon,
        requiredRole: 'organisation',
      },
      {
        id: 'billing',
        name: 'Billing',
        href: '/platform/billing',
        icon: CreditCardIcon,
        requiredRole: 'billing',
      },
      {
        id: 'plugins',
        name: 'Plugins',
        href: '/platform/plugins',
        icon: PuzzlePieceIcon,
        requiredRole: 'plugins',
      },
      {
        id: 'api',
        name: 'API / MCP',
        href: '/platform/api',
        icon: CodeBracketIcon,
        requiredRole: 'api',
      },
    ],
  },
];

export interface PermissionsData {
  isDemo?: boolean; // NEW - indicates if organization is in demo mode
  isAdmin?: boolean;
  roles?: string[];
  // Valuation permissions (all have View + Edit)
  canViewDashboard?: boolean;
  canEditDashboard?: boolean;
  canViewReports?: boolean;
  canEditReports?: boolean;
  canViewMethods?: boolean;
  canEditMethods?: boolean;
  canViewSales?: boolean;
  canEditSales?: boolean;
  canViewTransfer?: boolean;
  canEditTransfer?: boolean;
  canViewMarketing?: boolean;
  canEditMarketing?: boolean;
  canViewBrand?: boolean;
  canEditBrand?: boolean;
  canApprove?: boolean;
  // Sponsorships permissions (all have View + Edit)
  canViewSponsors?: boolean;
  canEditSponsors?: boolean;
  canViewPackages?: boolean;
  canEditPackages?: boolean;
  canViewInventory?: boolean;
  canEditInventory?: boolean;
  canViewProposals?: boolean;
  canEditProposals?: boolean;
  canViewAnalytics?: boolean;
  canEditAnalytics?: boolean;
  // Platform permissions (all have View + Edit)
  canViewUsers?: boolean;
  canEditUsers?: boolean;
  canViewOrganisation?: boolean;
  canEditOrganisation?: boolean;
  canViewAppSettings?: boolean;
  canEditAppSettings?: boolean;
  canViewPlugins?: boolean;
  canEditPlugins?: boolean;
  canViewBilling?: boolean;
  canEditBilling?: boolean;
  canViewAPI?: boolean;
  canEditAPI?: boolean;
  canViewAuditLog?: boolean;
  canEditAuditLog?: boolean;
  canViewSSO?: boolean;
  canEditSSO?: boolean;
  canViewDocuments?: boolean;
  canEditDocuments?: boolean;
}

export interface FeaturesData {
  sponsorshipSponsors?: boolean;
  sponsorshipPackages?: boolean;
  hasAdvancedReports?: boolean;
  hasGranularPermissions?: boolean;
  restApi?: boolean;
  auditLog?: boolean;
  documents?: boolean;
}

/**
 * Filter navigation tree based on permissions and plan features
 */
export function filterNavigationTree(
  nodes: NavigationNode[],
  permissions: PermissionsData | null | undefined,
  features: FeaturesData
): NavigationNode[] {
  // CRITICAL: If permissions not loaded, hide EVERYTHING - no exceptions
  // This prevents unauthorized access to ANY section when permissions can't be verified
  if (!permissions || !permissions.roles || !Array.isArray(permissions.roles)) {
    return [];
  }

  // Use centralized permission helpers (single source of truth)
  const hasAnyValuationPermission = checkHasAnyValuationPermission(permissions);
  const hasAnySponsorshipPermission = checkHasAnySponsorshipPermission(permissions);

  return nodes
    .map((node) => {
      const newNode = { ...node };

      // Feature flag filtering: Hide sponsorships module if feature flag is disabled
      if (node.id === 'sponsorships' && !isSponsorshipsEnabled()) {
        return null;
      }

      // Feature-based filtering
      if (node.feature) {
        const featureKey = node.feature as keyof typeof features;
        if (!features[featureKey]) {
          return null;
        }
      }

      // Special handling for Plugins - use canViewPlugins permission (allows demo users to see Plugins in menu)
      if (node.name === 'Plugins' && !permissions?.canViewPlugins) {
        return null;
      }

      // Special handling for Valuations - check if user can view any valuation section
      if (node.id === 'valuations' && !hasAnyValuationPermission) {
        return null;
      }

      // Special handling for Sponsorships - check if user can view any sponsorship section
      if (node.id === 'sponsorships' && !hasAnySponsorshipPermission) {
        return null;
      }

      // Plan-based filtering: Hide Reports if plan doesn't have it
      if (node.name === 'Reports' && !features.hasAdvancedReports) {
        return null;
      }

      // Plan-based filtering: Hide API / MCP if plan doesn't have it
      // (match on id — the item was renamed 'API' → 'API / MCP', which broke a name check)
      if (node.id === 'api' && !features.restApi) {
        return null;
      }

      // Permission-based filtering
      // CRITICAL: If permissions not loaded or roles array missing, treat as no permissions
      if (node.requiredRole) {
        // If no permissions data or no roles array, deny access
        if (!permissions || !permissions.roles || !Array.isArray(permissions.roles)) {
          return null;
        }

        // Allow access if:
        // 1. User is admin, OR
        // 2. User has the required role
        // Note: Demo users have admin role but canEdit is blocked via && !isDemo in permissions API
        // UPDATED: 2025-12-15 - Using hasRole helper (single source of truth)
        if (!hasRole(permissions, node.requiredRole as string)) {
          return null;
        }
      }

      // Filter children recursively
      if (newNode.children) {
        const filteredChildren = filterNavigationTree(newNode.children, permissions, features);
        // If no children remain after filtering, hide the parent
        if (filteredChildren.length === 0) {
          return null;
        }
        newNode.children = filteredChildren;
      }

      return newNode;
    })
    .filter((node): node is NavigationNode => node !== null);
}

/**
 * Get first href from navigation tree (depth-first search)
 */
export function getFirstHref(nodes: NavigationNode[]): string | undefined {
  for (const node of nodes) {
    if (node.href) return node.href;
    if (node.children) {
      const childHref = getFirstHref(node.children);
      if (childHref) return childHref;
    }
  }
  return undefined;
}

/**
 * Get the first accessible page based on permissions and plan features
 * Returns the first page URL from the filtered navigation tree
 * Defaults to /dashboard if no accessible page found (dashboard will show Access Denied)
 *
 * @param permissions - User permissions data
 * @param features - Plan features data
 * @returns First accessible page URL
 */
export function getFirstAccessiblePage(
  permissions: PermissionsData | null | undefined,
  features: FeaturesData
): string {
  const filteredNavigation = filterNavigationTree(navigationTree, permissions, features);
  const firstPage = getFirstHref(filteredNavigation);

  // Fallback to dashboard if no accessible page found
  // Dashboard will handle showing Access Denied if user has no permissions
  return firstPage || '/dashboard';
}
